import { $, footerHtml } from "../core/dom.js";
import { state } from "../core/state.js";
import { rpc } from "../core/supabaseClient.js";
import { clearTimersFor } from "../core/format.js";
import { markTab } from "../core/layout.js";
import { toast } from "../core/toast.js";
import { renderBracketToggle, renderLeagueSelector, currentBracket } from "../core/switcher.js";
import { communityBlockHtml } from "../core/community.js";
import { installHelpPossible, installDismissed } from "../core/install.js";
import { discordTipsPossible, discordDismissed } from "../core/discordHint.js";

// "Show install tips" (onboarding.md Batch 3, item 3 amendment): the undo
// for "Don't show again". Checked = tips allowed; unchecking sets the same
// device flag. Absent where install help can never apply: running as the
// installed app, or not a phone.
function installTipsHtml(){
  if (!installHelpPossible()) return "";
  return `<label class="tips-switch"><input type="checkbox" id="install-tips" ${installDismissed() ? "" : "checked"}
    onchange="setInstallTips(this.checked)"> Show install tips</label>`;
}

// "Show Discord tips" (discord-routing.md, Part 2, D5): the undo for the
// Discord bar's "Don't show again" and for Join, for the CURRENT league
// only. Checked = tips allowed; unchecking sets that league's flag. Absent
// where the league has no valid Discord link — live or not, since the switch
// undoes a dismissal and a webhook that is down today may be back tomorrow.
function discordTipsHtml(){
  if (!discordTipsPossible()) return "";
  return `<label class="tips-switch"><input type="checkbox" id="discord-tips" ${discordDismissed(state.currentLeagueId) ? "" : "checked"}
    onchange="setDiscordTips(this.checked)"> Show Discord tips</label>`;
}

// Shared between the standalone Settings tab (non-admins) and a section
// inside the Admin tab (admins) — one panel, two mounting points, so the
// toggle/league-selector/logout/credits/PIN-change form never has to be
// kept in sync twice.
export function settingsPanelHtml(){
  return `<div class="panel"><h2>Settings</h2>
    <div class="field"><label>Bracket</label><div class="switcher" id="bracketToggle"></div></div>
    <div id="leagueSelect"></div>
    <div id="leagueLinks"></div>
    ${discordTipsHtml()}
    ${installTipsHtml()}
    <p class="muted" style="margin-top:16px;font-weight:600">Change PIN</p>
    <div class="field"><label>Current PIN</label><input id="pin-current" inputmode="numeric" autocomplete="current-password" type="password" placeholder="••••"></div>
    <div class="field"><label>New PIN (4–8 digits)</label><input id="pin-new" inputmode="numeric" autocomplete="new-password" type="password" placeholder="••••"></div>
    <div class="field"><label>Confirm new PIN</label><input id="pin-confirm" inputmode="numeric" autocomplete="new-password" type="password" placeholder="••••"></div>
    <button class="btn ghost small" onclick="changeOwnPin()">Change PIN</button>
    <div class="err" id="pin-err"></div>
    <button class="btn ghost" style="margin-top:16px" onclick="logout()">Log out</button>
    <div class="credits">
      <p>Fantasy Eggy is an unofficial fan project — not affiliated with, endorsed by, or sponsored by Eggy or their management. Band names and song titles belong to their respective owners.</p>
      <p>Setlist data from <a href="https://thecarton.net" target="_blank" rel="noopener">The Carton</a>.</p>
      <p class="merch-plug"><a href="https://shop.eggymusic.com/" target="_blank" rel="noopener">Grab some merch</a> — it goes a long way toward keeping the band on the road.</p>
      <p class="colophon">Created by Kyle McKinley</p>
      <p class="colophon buildid">build ${__BUILD_ID__}</p>
    </div>
  </div>`;
}
// Must run after the html above is in the DOM — both render targets call
// this right after setting innerHTML.
export function wireSettingsPanel(){
  renderBracketToggle();
  renderLeagueSelector();
  renderLeagueLinks();
}
// Fifth surface for the per-league community links (standings-ux.md
// decision 1): the CURRENT league's fb_url/discord_url, read straight off
// the my_leagues row that state.leagues already holds — no fetch, no RPC.
// Goes through the same communityBlockHtml as the no-league and login
// screens (one https-only guard), adapted to that helper's {name, config}
// shape. Its own slot, not inside #leagueSelect: renderLeagueSelector
// renders nothing for a single-league player, and they still get the
// links. Empty when the league has no valid link — no heading over nothing.
// Inside the admin tab this sits a few sections below the Community
// editor; that redundancy is accepted (decision 1).
export function renderLeagueLinks(){
  const el = $("#leagueLinks");
  if (!el) return;
  const row = currentBracket();
  el.innerHTML = row
    ? communityBlockHtml([{ name: row.league_name, config: row }], { id: "settings-links", lead: "Community" })
    : "";
}
export async function renderSettings(){
  clearTimersFor("admin"); state.tab = "admin"; markTab();
  $("#main").innerHTML = settingsPanelHtml() + footerHtml();
  wireSettingsPanel();
}
// Voluntary self-service PIN change — the other half of decision 3 (forgot-
// PIN: self-service change + an admin reset button). change_own_pin already
// existed for the forced interstitial (submitForcedPinChange, auth.js); this
// is the same RPC, just reachable without a pending must_change_pin flag.
// Requires and verifies the CURRENT PIN server-side (change_own_pin calls
// _auth_player(p_name, p_pin) before anything else — a wrong current PIN
// raises before any write happens), so a live session on a shared device
// can't be used to lock the account owner out.
export async function changeOwnPin(){
  $("#pin-err").textContent = "";
  const current = $("#pin-current").value;
  const next = $("#pin-new").value, confirmPin = $("#pin-confirm").value;
  if (next !== confirmPin){ $("#pin-err").textContent = "New PINs don't match."; return; }
  try{
    await rpc("change_own_pin", { p_name: state.session.name, p_pin: current, p_new_pin: next });
    state.session = { ...state.session, pin: next, must_change_pin: false };
    localStorage.setItem("ft_session", JSON.stringify(state.session));
    $("#pin-current").value = ""; $("#pin-new").value = ""; $("#pin-confirm").value = "";
    toast("PIN changed ✔", "score");
  }catch(e){ $("#pin-err").textContent = e.message; }
}
